// src/services/initialInventoryAPI.ts
// Initial Inventory API service for qmLEO
// Handles creating inventory without purchase orders (onboarding & found items)

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';

const initialInventoryLogger = logger.createServiceLogger('InitialInventoryAPI');

// Acquisition source types
export type AcquisitionSource = 'PURCHASE_ORDER' | 'INITIAL_INVENTORY' | 'FOUND' | 'TRANSFER_IN';

// Request types
export interface SerializedItemInput {
  serialNumber: string;
  condition?: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Needs Repair';
  location?: string;
  subLocation?: string;
  notes?: string;
  photos?: string[];
  bodyArmor?: {
    ballisticLevel: string;
    size: string;
    expirationDate: string;
  };
}

export interface CreateSerializedRequest {
  productId: string;
  categoryId: string;
  subcategoryId: string;
  items: SerializedItemInput[];
  defaultLocation?: string;
  defaultCondition?: string;
  acquisitionDate?: string;
  acquisitionNotes?: string;
  acquisitionSource?: AcquisitionSource;
}

export interface CreateSerializedResponse {
  inventoryIds: string[];
  productId: string;
  productName: string;
  categoryId: string;
  subcategoryId: string;
  itemsCreated: number;
  itemsRequested: number;
  acquisitionSource: AcquisitionSource;
  errors?: Array<{
    index: number;
    serialNumber?: string;
    error: string;
  }>;
  partialSuccess?: boolean;
}

export interface CreateBulkRequest {
  productId: string;
  categoryId: string;
  subcategoryId: string;
  quantity: number;
  location: string;
  subLocation?: string;
  minStockLevel?: number;
  lotNumber?: string;
  expirationDate?: string;
  acquisitionDate?: string;
  acquisitionNotes?: string;
  acquisitionSource?: AcquisitionSource;
  notes?: string;
}

export interface CreateBulkResponse {
  inventoryId: string;
  productId: string;
  productName: string;
  categoryId: string;
  subcategoryId: string;
  totalQuantity: number;
  availableQuantity: number;
  location: string;
  acquisitionSource: AcquisitionSource;
}

export interface SessionSummaryItem {
  itemId: string;
  productId: string;
  name: string;
  itemType: string;
  quantity: number;
  serialNumber?: string;
  location: string;
}

export interface SessionSummaryCategory {
  categoryId: string;
  subcategoryId: string;
  items: SessionSummaryItem[];
  totalCount: number;
}

export interface SessionSummaryResponse {
  since: string;
  totalItems: number;
  totalQuantity: number;
  byCategory: SessionSummaryCategory[];
}

// API response wrapper
interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class InitialInventoryAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const endTimer = logger.startTimer('InitialInventoryAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('InitialInventoryAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    const headers = new Headers(options.headers || {});
    if (options.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('InitialInventoryAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const data = await response.json();

      if (!response.ok) {
        initialInventoryLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error: data.error || {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}`,
          },
        };
      }

      return data as APIResponse<T>;
    } catch (error: any) {
      endTimer();
      initialInventoryLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  /**
   * Create serialized inventory items (initial inventory or found items)
   */
  async createSerializedItems(request: CreateSerializedRequest): Promise<CreateSerializedResponse> {
    initialInventoryLogger.info('Creating initial serialized items', { 
      productId: request.productId,
      itemCount: request.items.length,
      acquisitionSource: request.acquisitionSource || 'INITIAL_INVENTORY'
    });

    const response = await this.makeRequest<CreateSerializedResponse>('/initial-inventory/serialized', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create serialized items');
    }

    initialInventoryLogger.info('Serialized items created', { 
      created: response.data.itemsCreated,
      requested: response.data.itemsRequested,
      errors: response.data.errors?.length || 0
    });
    
    return response.data;
  }

  /**
   * Create bulk inventory stock (initial inventory or found items)
   */
  async createBulkStock(request: CreateBulkRequest): Promise<CreateBulkResponse> {
    initialInventoryLogger.info('Creating initial bulk stock', { 
      productId: request.productId,
      quantity: request.quantity,
      location: request.location,
      acquisitionSource: request.acquisitionSource || 'INITIAL_INVENTORY'
    });

    const response = await this.makeRequest<CreateBulkResponse>('/initial-inventory/bulk', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create bulk stock');
    }

    initialInventoryLogger.info('Bulk stock created', { 
      inventoryId: response.data.inventoryId,
      quantity: response.data.totalQuantity
    });
    
    return response.data;
  }

  /**
   * Get session summary of items added since a given timestamp
   */
  async getSessionSummary(since: string): Promise<SessionSummaryResponse> {
    initialInventoryLogger.debug('Fetching session summary', { since });

    const response = await this.makeRequest<SessionSummaryResponse>(
      `/initial-inventory/session-summary?since=${encodeURIComponent(since)}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch session summary');
    }

    initialInventoryLogger.info('Session summary fetched', { 
      totalItems: response.data.totalItems,
      totalQuantity: response.data.totalQuantity
    });
    
    return response.data;
  }

  /**
   * Helper: Create a found item (convenience method with FOUND acquisition source)
   */
  async addFoundSerializedItem(
    productId: string,
    categoryId: string,
    subcategoryId: string,
    serialNumber: string,
    location: string,
    notes?: string
  ): Promise<CreateSerializedResponse> {
    return this.createSerializedItems({
      productId,
      categoryId,
      subcategoryId,
      items: [{ serialNumber, location, notes }],
      acquisitionSource: 'FOUND',
      acquisitionNotes: notes || 'Found item added to inventory'
    });
  }

  /**
   * Helper: Create found bulk items (convenience method with FOUND acquisition source)
   */
  async addFoundBulkItems(
    productId: string,
    categoryId: string,
    subcategoryId: string,
    quantity: number,
    location: string,
    notes?: string
  ): Promise<CreateBulkResponse> {
    return this.createBulkStock({
      productId,
      categoryId,
      subcategoryId,
      quantity,
      location,
      acquisitionSource: 'FOUND',
      acquisitionNotes: notes || 'Found items added to inventory',
      notes
    });
  }
}

// Export singleton instance
export const initialInventoryAPI = new InitialInventoryAPI();
